import {AxiosInstance} from "axios";
import {BillOfMaterialsPreviewDetails, BomFinalise} from "../model/job/BillOfMaterialsPreviewDetails";
import {
	CreateUpdateAssetApiDto,
	CreateUpdateBillOfMaterialsPreviewAssetApiDto,
	MaterialUpdateApiDto,
	PriceApiDto
} from "./model/rest";
import {BillOfMaterialsFinaliseOptions} from "../components/job/billOfMaterials/details/FinalisingBillOfMaterialsModal";
import SuggestedAssetPrice from "../model/job/SuggestedAssetPrice";
import {Option} from "ts-option";
import {Bom} from "../model/job/Bom";
import {BillOfMaterialsPreview} from "../model/job/BillOfMaterialsPreview";

export default class JobBillOfMaterialsApi {
	constructor(public readonly axiosInstance: AxiosInstance) { }

	/**
	 * Fetch bill of materials previews for a job
	 * @param {string} jobId
	 */
	fetchJobBillOfMaterialsPreviews(jobId: string): Promise<BillOfMaterialsPreview[]> {
		const url = `/api/web/jobs/${jobId}/bom/preview/profit`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(BillOfMaterialsPreview.fromJson));
	}

	/**
	 * Fetch job bill of materials details
	 * @param {string} jobId
	 * @returns {Promise<BillOfMaterialsPreviewDetails>}
	 */
	fetchJobBillOfMaterialsDetails(jobId: string): Promise<Bom> {
		const url = `/api/web/jobs/${jobId}/bom`;
		return this.axiosInstance.get(url).then(resp => Bom.fromJson(resp.data));
	}

	/**
	 * Fetch preview of job bill of materials details
	 * @param {string} jobId
	 * @param {string} merchantId
	 * @returns {Promise<BillOfMaterialsPreviewDetails>}
	 */
	fetchJobBillOfMaterialsPreview(jobId: string, merchantId: string): Promise<BillOfMaterialsPreviewDetails> {
		const url = `/api/web/jobs/${jobId}/bom/preview?merchantId=${merchantId}`;
		return this.axiosInstance.get(url).then(resp => BillOfMaterialsPreviewDetails.fromJson(resp.data));
	}

	/**
	 * Create bill of materials
	 * @param jobId
	 * @param merchantId
	 * @returns {Promise<BillOfMaterialsPreviewDetails>}
	 */
	createBillOfMaterials(jobId: string, merchantId: string): Promise<Bom> {
		const url = `/api/web/jobs/${jobId}/bom`;
		return this.axiosInstance.post(url, {merchantId})
			.then(resp => Bom.fromJson(resp.data));
	}

	/**
	 * Cancel selection of bill of materials for job
	 * @param {string} bomId
	 * @returns {AxiosPromise}
	 */
	cancelBOMSelection(bomId: string) {
		const url = `/api/web/bom/${bomId}/cancel`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Add asset to bill of materials
	 * @param {string} bomId
	 * @param {string} assetId
	 * @returns {Promise<Bom>}
	 */
	addAssetToBillOfMaterials(bomId: string, assetId: string): Promise<Bom> {
		const url = `/api/web/bom/${bomId}/items`;
		return this.axiosInstance.post(url, {assetId}).then(resp => Bom.fromJson(resp.data));
	}

	/**
	 * Remove asset from bill of materials
	 * @param {string} bomId
	 * @param {string} bomItemId
	 * @returns {Promise<Bom>}
	 */
	removeAssetFromBillOfMaterials(bomId: string, bomItemId: string): Promise<Bom> {
		const url = `/api/web/bom/${bomId}/items/${bomItemId}`;
		return this.axiosInstance.delete(url).then(resp => Bom.fromJson(resp.data));
	}

	/**
	 * Update bill of materials asset
	 * @param {string} bomId
	 * @param {string} bomItemId
	 * @param {CreateUpdateAssetApiDto} data
	 * @returns {Promise<BillOfMaterialsPreviewDetails>}
	 */
	updateBillOfMaterialsAsset(bomId: string,
		bomItemId: string,
		data: CreateUpdateBillOfMaterialsPreviewAssetApiDto): Promise<Bom> {
		const url = `/api/web/bom/${bomId}/items/${bomItemId}`;
		return this.axiosInstance.put(url, data).then(resp => Bom.fromJson(resp.data));
	}

	/**
	 * Update bill of materials asset sell price
	 * @param {string} bomId
	 * @param {string} bomItemId
	 * @param {PriceApiDto} price
	 * @returns {Promise<BillOfMaterialsPreviewDetails>}
	 */
	updateBillOfMaterialsAssetSellPrice(bomId: string,
		bomItemId: string,
		price: PriceApiDto): Promise<Bom> {
		const url = `/api/web/bom/${bomId}/items/${bomItemId}/sell_price`;
		return this.axiosInstance.put(url, price).then(resp => Bom.fromJson(resp.data));
	}

	/**
	 * Add material to asset on bill of materials  suggested new sell price for asset
	 * @param bomId
	 * @param bomItemId
	 * @param materialId
	 * @returns {Promise<{suggestedAssetPrice: SuggestedAssetPrice, bom: Bom}>}
	 */
	addMaterialToAssetOnBillOfMaterials(bomId: string,
		bomItemId: string,
		materialId: string): Promise<{ suggestedAssetPrice: SuggestedAssetPrice, bom: Bom }> {
		const url = `/api/web/bom/${bomId}/items/${bomItemId}/materials`;
		return this.axiosInstance.post(url, {materialId}).then(resp => {
			return {
				suggestedAssetPrice: SuggestedAssetPrice.fromJson(resp.data.suggestedAssetPrice),
				bom: Bom.fromJson(resp.data.bom)
			}
		});
	}

	/**
	 * Remove asset from bill of materials
	 * @param {string} bomId
	 * @param {string} bomItemId
	 * @param {string} bomMaterialId
	 * @returns {Promise<{suggestedAssetPrice: SuggestedAssetPrice, bom: Bom}>}
	 */
	removeMaterialFromAssetOnBillOfMaterials(bomId: string,
		bomItemId: string,
		bomMaterialId: string): Promise<{ suggestedAssetPrice: SuggestedAssetPrice, bom: Bom }> {
		const url = `/api/web/bom/${bomId}/items/${bomItemId}/materials/${bomMaterialId}`;
		return this.axiosInstance.delete(url).then(resp => {
			return {
				suggestedAssetPrice: SuggestedAssetPrice.fromJson(resp.data.suggestedAssetPrice),
				bom: Bom.fromJson(resp.data.bom)
			}
		});
	}

	/**
	 * Get bill of materials finalised calculations
	 * @param {string} bomId
	 * @returns {Promise<BomFinalise>}
	 */
	getFinalisedCalculations(bomId: string): Promise<BomFinalise[]> {
		const url = `/api/web/bom/${bomId}/finalisedCalculations`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(BomFinalise.fromJson));
	}

	/**
	 * Generate Bill - finalise Bill of Materials
	 * @param {string} bomId
	 * @param data
	 * @returns {Promise<Bom>}
	 */
	finaliseBillOfMaterials(bomId: string, data: Option<BillOfMaterialsFinaliseOptions>): Promise<Bom> {
		const url = `/api/web/bom/${bomId}/finalize`;
		return this.axiosInstance.post(url,
			data.map(d => {return {adjust: d}}).orNull,
			{headers: {'Content-Type': 'application/json'}}).then(resp => Bom.fromJson(resp.data));
	}

	updateBillOfMaterials(bomId: string, merchantBranchLocationId: string) {
		const url = `/api/web/bom/${bomId}`;
		const updatedData = {merchantBranchLocationId};
		return this.axiosInstance.put(url, updatedData).then(resp => Bom.fromJson(resp.data));
	}

	/**
	 * Update material.
	 * @param materialId
	 * @param data
	 */
	updateMaterial(materialId: string, data: MaterialUpdateApiDto) {
		const url = `/api/web/materials/${materialId}`;
		return this.axiosInstance.put(url, data);
	}
}
